import { z } from 'zod';
import { defineCollection, type RecordOf } from '../collection.ts';
import { id, longText, requiredText, shortText } from '../fields.ts';

export const emergencyContacts = defineCollection({
  name: 'emergency_contacts',
  module: 'emergency',
  label: { one: 'emergency contact', other: 'emergency contacts' },
  schema: z.object({
    name: requiredText(120),
    relationship: shortText(80).default(''),
    phone: shortText(40).default(''),
    altPhone: shortText(40).default(''),
    email: shortText(200).default(''),
    address: shortText(300).default(''),
    notes: shortText(1_000).default(''),
    /** Lower numbers are listed first. */
    priority: z.number().int().min(0).max(999).default(100),
  }),
  defaultAudience: 'family',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: [],
  manualMergeFields: [],
});

export const EMERGENCY_INFO_KINDS = [
  'address',
  'medical',
  'allergy',
  'medication',
  'doctor',
  'insurance',
  'utility',
  'meeting_place',
  'school',
  'other',
] as const;

/**
 * User-entered essentials. Health details exist only when someone enters them
 * deliberately; nothing is inferred.
 */
export const emergencyInfo = defineCollection({
  name: 'emergency_info',
  module: 'emergency',
  label: { one: 'emergency note', other: 'emergency notes' },
  schema: z.object({
    kind: z.enum(EMERGENCY_INFO_KINDS).default('other'),
    label: requiredText(120),
    details: longText(5_000).default(''),
    phone: shortText(40).default(''),
    /** Which family member this is about, if anyone in particular. */
    memberId: id.nullable().default(null),
    priority: z.number().int().min(0).max(999).default(100),
  }),
  defaultAudience: 'adults',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: [],
  manualMergeFields: ['details'],
});

export type EmergencyContact = RecordOf<typeof emergencyContacts>;
export type EmergencyInfo = RecordOf<typeof emergencyInfo>;
